import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useToast } from '../contexts/ToastContext';
import exportService from '../services/exportService';
import Icon from './Icon';

/**
 * 시스템 카테고리 목록 (모바일 categories.js와 동일)
 * Material Icons 매핑 포함
 */
const SYSTEM_CATEGORIES = {
  expense: [
    { name: '식비', icon: 'restaurant' },
    { name: '주거비', icon: 'home' },
    { name: '교통비', icon: 'directions_bus' },
    { name: '통신비', icon: 'phone_android' },
    { name: '공과금', icon: 'bolt' },
    { name: '쇼핑', icon: 'shopping_bag' },
    { name: '생활용품', icon: 'shopping_basket' },
    { name: '뷰티/미용', icon: 'face' },
    { name: '여행/레저', icon: 'flight' },
    { name: '문화생활', icon: 'movie' },
    { name: '의료/약국', icon: 'medication' },
    { name: '교육/강의', icon: 'menu_book' },
    { name: '경조사비', icon: 'card_giftcard' },
    { name: '반려동물', icon: 'pets' },
    { name: '금융/투자', icon: 'show_chart' },
    { name: '기타', icon: 'more_horiz' },
  ],
  income: [
    { name: '급여', icon: 'payments' },
    { name: '부업', icon: 'work' },
    { name: '투자수익', icon: 'trending_up' },
    { name: '용돈', icon: 'account_balance_wallet' },
    { name: '상여금', icon: 'card_giftcard' },
    { name: '기타', icon: 'attach_money' },
  ],
};

const FORMAT_OPTIONS = [
  { value: 'csv', label: 'CSV' },
  { value: 'xlsx', label: 'Excel' },
];

const TYPE_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'income', label: '수입' },
  { value: 'expense', label: '지출' },
];

const QUICK_RANGES = [
  { key: 'all', label: '전체' },
  { key: '1m', label: '1개월' },
  { key: '3m', label: '3개월' },
  { key: '6m', label: '6개월' },
  { key: '1y', label: '1년' },
  { key: 'custom', label: '직접 선택' },
];

/**
 * Quick Range 키에 따른 시작/종료 날짜 계산
 * @param {string} key - Quick Range 키
 * @returns {{ start: string|null, end: string|null }}
 */
function getQuickRangeDates(key) {
  const now = new Date();
  const end = formatDate(now);
  let start = null;

  if (key === '1m') {
    const d = new Date(now);
    d.setMonth(d.getMonth() - 1);
    start = formatDate(d);
  } else if (key === '3m') {
    const d = new Date(now);
    d.setMonth(d.getMonth() - 3);
    start = formatDate(d);
  } else if (key === '6m') {
    const d = new Date(now);
    d.setMonth(d.getMonth() - 6);
    start = formatDate(d);
  } else if (key === '1y') {
    const d = new Date(now);
    d.setFullYear(d.getFullYear() - 1);
    start = formatDate(d);
  }

  return { start, end: key === 'all' ? null : end };
}

/**
 * Date 객체를 YYYY-MM-DD 문자열로 변환
 * @param {Date} date
 * @returns {string}
 */
function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * SegmentGroup — 큰 세그먼트 버튼 컴포넌트 (모바일 스타일)
 * pill 형태, 선택 시 primary 배경 + 흰색 텍스트
 */
const SegmentGroup = ({ options, value, onChange, ariaLabel }) => (
  <div className="segment-group" role="tablist" aria-label={ariaLabel}>
    {options.map((option) => (
      <button
        key={option.value}
        role="tab"
        aria-selected={value === option.value}
        className={`segment-btn ${value === option.value ? 'segment-btn--active' : ''}`}
        onClick={() => onChange(option.value)}
        type="button"
      >
        {option.label}
      </button>
    ))}
  </div>
);

SegmentGroup.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
};

/**
 * DataExport — 데이터 내보내기 페이지
 *
 * 모바일과 동일한 기능:
 * - 파일 형식 선택 (CSV / Excel)
 * - 거래 유형 필터 (전체 / 수입 / 지출)
 * - 기간 선택 (Quick Range Chips + 직접 선택)
 * - 카테고리 필터 (접힌 상태 → 펼침 accordion)
 * - 백엔드 API 호출로 내보내기 실행
 */
const DataExport = () => {
  const { showToast } = useToast();

  const [format, setFormat] = useState('csv');
  const [transactionType, setTransactionType] = useState('all');
  const [rangeKey, setRangeKey] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);

  // 시스템 카테고리 목록 (거래 유형에 따라 필터)
  const availableCategories = useMemo(() => {
    if (transactionType === 'income') return SYSTEM_CATEGORIES.income;
    if (transactionType === 'expense') return SYSTEM_CATEGORIES.expense;
    return [...SYSTEM_CATEGORIES.expense, ...SYSTEM_CATEGORIES.income];
  }, [transactionType]);

  // 카테고리 요약 텍스트
  const categorySummary = useMemo(() => {
    if (selectedCategories.length === 0) return '전체 카테고리';
    return `${selectedCategories.length}개 선택`;
  }, [selectedCategories]);

  // 거래 유형 변경 시 카테고리 선택 초기화
  const handleTypeChange = useCallback((type) => {
    setTransactionType(type);
    setSelectedCategories([]);
  }, []);

  // Quick Range 선택
  const handleRangeChip = useCallback((key) => {
    setRangeKey(key);
    if (key === 'custom') return;
    const { start, end } = getQuickRangeDates(key);
    setStartDate(start);
    setEndDate(end);
  }, []);

  // 직접 선택 날짜 변경
  const handleStartDateChange = useCallback((e) => {
    setStartDate(e.target.value || null);
    setRangeKey('custom');
  }, []);

  const handleEndDateChange = useCallback((e) => {
    setEndDate(e.target.value || null);
    setRangeKey('custom');
  }, []);

  // 카테고리 토글
  const toggleCategory = useCallback((name) => {
    setSelectedCategories((prev) =>
      prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]
    );
  }, []);

  // 전체 선택 / 해제
  const toggleAllCategories = useCallback(() => {
    setSelectedCategories((prev) =>
      prev.length === availableCategories.length
        ? []
        : availableCategories.map((cat) => cat.name)
    );
  }, [availableCategories]);

  // 카테고리 accordion 토글
  const toggleCategoryAccordion = useCallback(() => {
    setIsCategoryOpen((prev) => !prev);
  }, []);

  // 내보내기 실행
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    setError(null);

    try {
      const options = {
        format,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        categories: selectedCategories.length > 0 ? selectedCategories : undefined,
        type: transactionType !== 'all' ? transactionType : undefined,
      };

      await exportService.exportTransactions(options);
      showToast({ type: 'success', message: '파일이 성공적으로 다운로드되었습니다.' });
    } catch (err) {
      const userMessage = err.userMessage || '내보내기 중 오류가 발생했습니다.';
      const retriable = err.retriable !== false;

      // 데이터 없음 (404 또는 특정 코드)
      if (err.code === 'NOT_FOUND' || err.code === 'NO_DATA') {
        setError({
          message: '선택한 조건에 해당하는 데이터가 없습니다.',
          retriable: false,
        });
      } else {
        setError({ message: userMessage, retriable });
      }
    } finally {
      setIsExporting(false);
    }
  }, [format, startDate, endDate, selectedCategories, transactionType, showToast]);

  // 기간 요약 텍스트
  const rangeSummary = useMemo(() => {
    if (rangeKey === 'all') return '전체 기간';
    if (rangeKey === 'custom') {
      if (startDate && endDate) return `${startDate} ~ ${endDate}`;
      if (startDate) return `${startDate} ~`;
      return '날짜를 선택하세요';
    }
    const found = QUICK_RANGES.find((r) => r.key === rangeKey);
    return found ? `최근 ${found.label}` : '';
  }, [rangeKey, startDate, endDate]);

  return (
    <div className="data-export">
      <div className="data-export__header">
        <h2 className="data-export__title">
          <Icon name="download" size={24} />
          데이터 내보내기
        </h2>
        <p className="data-export__description">
          거래 내역을 파일로 내보내 스프레드시트에서 분석하세요.
        </p>
      </div>

      {/* 카드 1: 파일 형식 + 거래 유형 */}
      <div className="data-export__card">
        <div className="data-export__card-grid">
          <div className="data-export__field">
            <label className="data-export__label">
              <Icon name="description" size={16} />
              파일 형식
            </label>
            <SegmentGroup
              options={FORMAT_OPTIONS}
              value={format}
              onChange={setFormat}
              ariaLabel="파일 형식 선택"
            />
          </div>

          <div className="data-export__field">
            <label className="data-export__label">
              <Icon name="swap_vert" size={16} />
              거래 유형
            </label>
            <SegmentGroup
              options={TYPE_OPTIONS}
              value={transactionType}
              onChange={handleTypeChange}
              ariaLabel="거래 유형 선택"
            />
          </div>
        </div>
      </div>

      {/* 카드 2: 기간 설정 */}
      <div className="data-export__card">
        <label className="data-export__label">
          <Icon name="date_range" size={16} />
          기간
        </label>
        <div className="data-export__chips">
          {QUICK_RANGES.map((range) => (
            <button
              key={range.key}
              type="button"
              className={`data-export__chip ${rangeKey === range.key ? 'data-export__chip--active' : ''}`}
              onClick={() => handleRangeChip(range.key)}
              aria-pressed={rangeKey === range.key}
            >
              {range.label}
            </button>
          ))}
        </div>
        {rangeKey === 'custom' && (
          <div className="data-export__custom-range">
            <div className="data-export__date-field">
              <label className="data-export__date-label" htmlFor="export-start-date">
                시작일
              </label>
              <input
                id="export-start-date"
                type="date"
                className="data-export__date-input"
                value={startDate || ''}
                onChange={handleStartDateChange}
              />
            </div>
            <span className="data-export__date-separator">~</span>
            <div className="data-export__date-field">
              <label className="data-export__date-label" htmlFor="export-end-date">
                종료일
              </label>
              <input
                id="export-end-date"
                type="date"
                className="data-export__date-input"
                value={endDate || ''}
                onChange={handleEndDateChange}
              />
            </div>
          </div>
        )}
        {rangeKey !== 'custom' && rangeKey !== 'all' && (
          <p className="data-export__range-summary">{rangeSummary}</p>
        )}
      </div>

      {/* 카드 3: 카테고리 (접힌 상태) */}
      <div className="data-export__card data-export__card--category">
        <button
          type="button"
          className="data-export__category-toggle"
          onClick={toggleCategoryAccordion}
          aria-expanded={isCategoryOpen}
          aria-controls="data-export-category-panel"
        >
          <span className="data-export__category-toggle-left">
            <Icon name="category" size={16} />
            <span className="data-export__category-toggle-label">카테고리</span>
            {selectedCategories.length > 0 && (
              <span className="data-export__badge">{selectedCategories.length}</span>
            )}
          </span>
          <span className="data-export__category-toggle-right">
            <span className="data-export__category-toggle-summary">
              {categorySummary}
            </span>
            {isCategoryOpen && (
              <button
                type="button"
                className="data-export__select-all-inline"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleAllCategories();
                }}
                aria-label={
                  selectedCategories.length === availableCategories.length
                    ? '전체 해제'
                    : '전체 선택'
                }
              >
                {selectedCategories.length === availableCategories.length ? '전체 해제' : '전체 선택'}
              </button>
            )}
            <Icon
              name={isCategoryOpen ? 'expand_less' : 'chevron_right'}
              size={20}
              className="data-export__category-chevron"
            />
          </span>
        </button>

        {isCategoryOpen && (
          <div
            id="data-export-category-panel"
            className="data-export__category-panel"
            role="region"
            aria-label="카테고리 선택"
          >
            <div className="data-export__category-list">
              {transactionType === 'all' && (
                <div className="data-export__category-section-header">
                  <span className="data-export__category-section-title">지출 카테고리</span>
                  <span className="data-export__category-section-divider" />
                </div>
              )}
              {(transactionType === 'all' ? SYSTEM_CATEGORIES.expense : []).map((cat) => {
                const isSelected = selectedCategories.includes(cat.name);
                return (
                  <button
                    key={`expense-${cat.name}`}
                    type="button"
                    className={`data-export__category-item ${isSelected ? 'data-export__category-item--active' : ''}`}
                    onClick={() => toggleCategory(cat.name)}
                    aria-pressed={isSelected}
                  >
                    <Icon
                      name={cat.icon}
                      size={20}
                      className="data-export__category-item-icon"
                    />
                    <span className="data-export__category-item-name">{cat.name}</span>
                    {isSelected && (
                      <Icon
                        name="check"
                        size={18}
                        className="data-export__category-item-check"
                      />
                    )}
                  </button>
                );
              })}
              {transactionType === 'all' && (
                <div className="data-export__category-section-header">
                  <span className="data-export__category-section-title">수입 카테고리</span>
                  <span className="data-export__category-section-divider" />
                </div>
              )}
              {(transactionType === 'all' ? SYSTEM_CATEGORIES.income : []).map((cat) => {
                const isSelected = selectedCategories.includes(cat.name);
                return (
                  <button
                    key={`income-${cat.name}`}
                    type="button"
                    className={`data-export__category-item ${isSelected ? 'data-export__category-item--active' : ''}`}
                    onClick={() => toggleCategory(cat.name)}
                    aria-pressed={isSelected}
                  >
                    <Icon
                      name={cat.icon}
                      size={20}
                      className="data-export__category-item-icon"
                    />
                    <span className="data-export__category-item-name">{cat.name}</span>
                    {isSelected && (
                      <Icon
                        name="check"
                        size={18}
                        className="data-export__category-item-check"
                      />
                    )}
                  </button>
                );
              })}
              {transactionType !== 'all' && availableCategories.map((cat) => {
                const isSelected = selectedCategories.includes(cat.name);
                return (
                  <button
                    key={cat.name}
                    type="button"
                    className={`data-export__category-item ${isSelected ? 'data-export__category-item--active' : ''}`}
                    onClick={() => toggleCategory(cat.name)}
                    aria-pressed={isSelected}
                  >
                    <Icon
                      name={cat.icon}
                      size={20}
                      className="data-export__category-item-icon"
                    />
                    <span className="data-export__category-item-name">{cat.name}</span>
                    {isSelected && (
                      <Icon
                        name="check"
                        size={18}
                        className="data-export__category-item-check"
                      />
                    )}
                  </button>
                );
              })}
            </div>
            {selectedCategories.length === 0 && (
              <p className="data-export__category-hint">
                선택하지 않으면 전체 카테고리가 포함됩니다.
              </p>
            )}
          </div>
        )}
      </div>

      {/* 에러 표시 */}
      {error && (
        <div className="data-export__error" role="alert">
          <Icon name="error_outline" size={18} />
          <span>{error.message}</span>
          {error.retriable && (
            <button
              type="button"
              className="data-export__retry-btn"
              onClick={handleExport}
            >
              다시 시도
            </button>
          )}
        </div>
      )}

      {/* 내보내기 버튼 */}
      <div className="data-export__footer">
        <button
          type="button"
          className="btn-primary data-export__export-btn"
          onClick={handleExport}
          disabled={isExporting}
          aria-label="데이터 내보내기"
        >
          {isExporting ? (
            <>
              <span className="data-export__spinner" aria-hidden="true" />
              내보내는 중...
            </>
          ) : (
            <>
              <Icon name="download" size={18} />
              {format === 'csv' ? 'CSV' : 'Excel'} 내보내기
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default DataExport;
